import { useState, useRef, useEffect, CSSProperties } from 'react';
import { motion } from 'framer-motion';

const FinalSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    
    // Configurar o observer para detectar quando o componente está visível
    let observer: IntersectionObserver | null = null;
    
    // Pequeno atraso para evitar problemas de timing
    const timer = setTimeout(() => {
      if (currentRef) {
        // Definir um threshold menor para dispositivos móveis
        const isMobile = window.innerWidth < 768;
        const threshold = isMobile ? 0.05 : 0.3;
        
        observer = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;
            setIsInView(entry.isIntersecting);
          },
          { 
            threshold: threshold,
            rootMargin: isMobile ? '150px 0px' : '0px'
          }
        );
        
        observer.observe(currentRef);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  const sectionStyle: CSSProperties = {
    position: 'relative',
    padding: '5rem 0 0 0',
    zIndex: 10,
    backgroundImage: 'url("/images/footer-background.png")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };

  const overlayStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: -1,
  };

  const containerStyle: CSSProperties = {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 1rem',
  };

  const contentStyle: CSSProperties = {
    maxWidth: '48rem',
    margin: '0 auto',
    textAlign: 'center',
  };

  const titleStyle: CSSProperties = {
    fontSize: '2.25rem',
    fontWeight: 'bold',
    color: 'white',
    marginBottom: '3rem',
    fontFamily: 'Kobe, sans-serif'
  };

  const buttonsContainerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: window.innerWidth < 640 ? 'column' : 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1.5rem',
    marginBottom: '4rem',
  };

  const buttonStyle: CSSProperties = {
    backgroundColor: 'white',
    color: '#2563eb',
    padding: '1rem 2.5rem',
    borderRadius: '0.5rem',
    fontWeight: '500',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    display: 'inline-block',
  };

  const copyrightContainerStyle: CSSProperties = {
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    padding: '1rem 0',
    marginTop: '4rem',
  };
  
  const copyrightStyle: CSSProperties = {
    fontSize: '0.875rem',
    color: 'rgba(255, 255, 255, 0.7)',
    textAlign: 'center',
  };

  return (
    <section ref={ref} style={sectionStyle}>
      <div style={overlayStyle}></div>
      <div style={containerStyle}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          style={contentStyle}
        >
          <motion.h2
            initial={{ opacity: 0, y: -10 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -10 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={titleStyle}
            className="title-font"
          >
            Consulte o regulamento e política do site
          </motion.h2>

          <div style={buttonsContainerStyle}>
            <motion.a
              href={`/files/REGULAMENTO HACKATHON B2B 2025 - ${document.documentElement.getAttribute('data-regiao') || 'APAC'}.pdf`}
              download
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              style={buttonStyle}
            >
              Regulamento
            </motion.a>

            <motion.a
              href={`/files/POLÍTICA DE PRIVACIDADE HACKATHON B2B 2025 - ${document.documentElement.getAttribute('data-regiao') || 'APAC'}.pdf`}
              download
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              style={buttonStyle}
            >
              Política de Privacidade
            </motion.a>
          </div>
        </motion.div>
      </div>
      
      <div style={copyrightContainerStyle}>
        <div style={containerStyle}>
          <motion.p
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            style={copyrightStyle}
          >
            2025 Happy Escolas. Todos os direitos reservados.
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default FinalSection; 